/**
 * AddBehaviorNoteForm (#553) — the Pet Profile's single write path. Posts to
 * POST /api/admin/dogs/:dogId/behavior-note (already tenant-scoped server-side)
 * and, on success, asks the page to refetch so the new note appears with the
 * same full-fidelity shape as the rest of the list (no optimistic shape drift).
 *
 * Lane A owns this — it does NOT import the Lane-B `BehaviorNoteModal`
 * (components/dogs/*). Five states: default · submitting (disabled) · error
 * (what happened + the input is preserved to retry) · success (closes).
 *
 * Sawyer 12 incident-wording-pet-notes (F3): this is the "Pet note" form. It
 * opens in view with the text box focused, and says "pet parent", never
 * "owner", for the dog's family.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  addDogBehaviorNote,
  humanizeApiError,
  readSession,
  type DogBehaviorNoteType,
  type DogNoteAudience,
} from '../../lib/api.js';

interface AddBehaviorNoteFormProps {
  apiUrl: string;
  dogId: string;
  /** Called after a successful create so the page reloads the profile. */
  onCreated: () => void;
  onCancel: () => void;
}

const TYPE_OPTIONS: ReadonlyArray<{ value: DogBehaviorNoteType; label: string }> =
  [
    { value: 'info', label: 'Info' },
    { value: 'positive', label: 'Positive' },
    { value: 'concern', label: 'Concern' },
  ];

// #714 — the privacy default lives at the point of capture: an untouched
// form produces a STAFF-ONLY note; sharing with the owner is the explicit
// act. Order matters — the default option renders first.
const AUDIENCE_OPTIONS: ReadonlyArray<{
  value: DogNoteAudience;
  label: string;
}> = [
  { value: 'staff_only', label: 'Staff only' },
  { value: 'owner_visible', label: 'Shared with pet parent' },
];

export function AddBehaviorNoteForm({
  apiUrl,
  dogId,
  onCreated,
  onCancel,
}: AddBehaviorNoteFormProps): JSX.Element {
  const [note, setNote] = useState('');
  const [type, setType] = useState<DogBehaviorNoteType>('info');
  const [audience, setAudience] = useState<DogNoteAudience>('staff_only');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Sawyer F3 — the form opens in view with the text box focused, from either
  // "Add pet note" trigger. No smooth scroll under prefers-reduced-motion.
  useEffect(() => {
    textRef.current?.focus({ preventScroll: true });
    const form = formRef.current;
    if (form && typeof form.scrollIntoView === 'function') {
      const reduce =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      form.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    }
  }, []);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = note.trim();
    if (trimmed.length === 0) {
      setError('Enter a note before saving.');
      textRef.current?.focus();
      return;
    }
    const { token } = readSession();
    if (!token) {
      setError('Your session has expired — sign in again to add a note.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await addDogBehaviorNote(apiUrl, token, dogId, {
        note: trimmed,
        type,
        audience,
      });
      onCreated();
    } catch (err) {
      const raw =
        err instanceof Error ? err.message : 'Failed to save the pet note';
      setError(humanizeApiError(raw));
      setSubmitting(false);
    }
  };

  return (
    <form
      ref={formRef}
      className="ferro-dogprofile__note-form"
      onSubmit={onSubmit}
      data-testid="dogprofile-note-form"
    >
      <label
        className="ferro-dogprofile__field-label"
        htmlFor="dogprofile-note-text"
      >
        Pet note
      </label>
      <textarea
        id="dogprofile-note-text"
        ref={textRef}
        className="ferro-dogprofile__textarea"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        maxLength={2000}
        placeholder="What happened? (e.g. Played well with the small-dog group.)"
        disabled={submitting}
      />
      <div className="ferro-dogprofile__note-row">
        <label
          className="ferro-dogprofile__field-label"
          htmlFor="dogprofile-note-type"
        >
          Type
        </label>
        <select
          id="dogprofile-note-type"
          className="ferro-dogprofile__select"
          value={type}
          onChange={(e) => setType(e.target.value as DogBehaviorNoteType)}
          disabled={submitting}
        >
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div className="ferro-dogprofile__note-row">
        <label
          className="ferro-dogprofile__field-label"
          htmlFor="dogprofile-note-audience"
        >
          Visibility
        </label>
        <select
          id="dogprofile-note-audience"
          className="ferro-dogprofile__select"
          value={audience}
          onChange={(e) => setAudience(e.target.value as DogNoteAudience)}
          disabled={submitting}
          aria-describedby="dogprofile-note-audience-help"
        >
          {AUDIENCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      {/* Honest state, always on: which channel this note will land in. */}
      <p
        id="dogprofile-note-audience-help"
        className="ferro-dogprofile__note-audience-help"
      >
        {audience === 'staff_only'
          ? 'Stays with staff. The pet parent never sees it.'
          : "Appears in the pet parent's app."}
      </p>
      {error && (
        <p className="ferro-dogprofile__form-error" role="alert">
          {error}
        </p>
      )}
      <div className="ferro-dogprofile__note-actions">
        <button
          type="submit"
          className="ferro-dogprofile__btn ferro-dogprofile__btn--primary"
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'Save note'}
        </button>
        <button
          type="button"
          className="ferro-dogprofile__btn ferro-dogprofile__btn--ghost"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
